import {
  removeSessionUuid,
  removeToken,
  setSessionUuidLocalStorage,
  setSessionUuidSessionStorage,
  setTokenLocalStorage,
  setTokenSessionStorage,
} from '@config/storage';
import { OrganizationSummary, User, UserPreferences } from '@models/user.model';

export const UserTypes = {
  SET_USER_DATA: 'SET_USER_DATA',
  SET_USER_DETAILS: 'SET_USER_DETAILS',
  SET_USER_ORGANIZATIONS: 'SET_USER_ORGANIZATIONS',
  SET_USER_PREFERENCES: 'SET_USER_PREFERENCES',
  LOGOUT_USER: 'LOGOUT_USER',
  UPDATE_SIDEBAR_STATUS: 'UPDATE_SIDEBAR_STATUS',
  SET_IMPERSONATING: 'SET_IMPERSONATING',
  SET_SHOW_TUTORIAL: 'SET_SHOW_TUTORIAL',
  SET_ONBOARDING_SPOTLIGHT: 'SET_ONBOARDING_SPOTLIGHT',
};

export const setUserData = (token: string, rememberMe: boolean, sessionUuid: string) => {
  if (rememberMe) {
    setTokenLocalStorage(token);
    if (sessionUuid) {
      setSessionUuidLocalStorage(sessionUuid);
    }
  } else {
    setTokenSessionStorage(token);
    if (sessionUuid) {
      setSessionUuidSessionStorage(sessionUuid);
    }
  }
  return {
    type: UserTypes.SET_USER_DATA,
    data: { token, rememberMe },
  };
};

export const setUserDetails = (details: Partial<User>) => {
  return {
    type: UserTypes.SET_USER_DETAILS,
    data: details,
  };
};

export const setUserOrganizations = (organizations: OrganizationSummary[]) => {
  return {
    type: UserTypes.SET_USER_ORGANIZATIONS,
    data: { organizations },
  };
};

export const setUserPreferences = (preferences: UserPreferences) => {
  return {
    type: UserTypes.SET_USER_PREFERENCES,
    data: { preferences },
  };
};

export const updateSidebarStatus = () => {
  return {
    type: UserTypes.UPDATE_SIDEBAR_STATUS,
    data: {},
  };
};

export const setImpersonating = (isImpersonating: boolean) => {
  return {
    type: UserTypes.SET_IMPERSONATING,
    data: { isImpersonating },
  };
};

export const setShowTutorial = (showTutorial: boolean, startStep?: number) => {
  return {
    type: UserTypes.SET_SHOW_TUTORIAL,
    data: { showTutorial, tutorialStartStep: startStep ?? 0 },
  };
};

export interface OnboardingSpotlightData {
  stepId: string;
  targetSelector: string;
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export const setOnboardingSpotlight = (spotlight: OnboardingSpotlightData | null) => {
  return {
    type: UserTypes.SET_ONBOARDING_SPOTLIGHT,
    data: { onboardingSpotlight: spotlight },
  };
};

export const logoutUser = () => {
  removeSessionUuid();
  removeToken();
  //TODO: Check if we want to implement it here
  // window.open(redirectUrl || process.env.REACT_APP_LOGOUT_URL, '_self');
  return {
    type: UserTypes.LOGOUT_USER,
    data: {},
  };
};

export type UserState = {
  isLoggedIn: boolean;
  collapsedSidebar?: boolean;
  firstName?: string | undefined | null;
  lastName?: string | undefined | null;
  role: string | null;
  avatar?: string | null;
  rememberMe?: boolean;
  userId?: string | null;
  email?: string | null;
  organizations?: OrganizationSummary[] | null;
  defaultOrganizationId?: string | null;
  preferences?: UserPreferences | null;
  isImpersonating?: boolean;
  showTutorial?: boolean;
  tutorialStartStep?: number;
  onboardingSpotlight?: OnboardingSpotlightData | null;
};

// INITIAL STATE
const userInitialState: UserState = {
  isLoggedIn: false,
  collapsedSidebar: false,
  role: null,
  avatar: '',
  firstName: undefined,
  lastName: undefined,
  rememberMe: false,
  userId: null,
  email: null,
  organizations: null,
  defaultOrganizationId: null,
  preferences: null,
  isImpersonating: false,
  showTutorial: false,
  tutorialStartStep: 0,
  onboardingSpotlight: null,
};

export type UserAction = {
  type: string;
  data: any;
};

const userReducer = (state = userInitialState, action: UserAction) => {
  switch (action.type) {
    case UserTypes.SET_USER_DATA:
      return {
        ...state,
        isLoggedIn: true,
        rememberMe: action.data.rememberMe,
      };

    case UserTypes.SET_USER_DETAILS:
      return {
        ...state,
        ...action.data,
        isLoggedIn: true,
      };

    case UserTypes.SET_USER_ORGANIZATIONS:
      return {
        ...state,
        organizations: action.data.organizations,
      };

    case UserTypes.SET_USER_PREFERENCES:
      return {
        ...state,
        preferences: action.data.preferences,
      };

    case UserTypes.UPDATE_SIDEBAR_STATUS:
      return {
        ...state,
        collapsedSidebar: !state.collapsedSidebar,
      };

    case UserTypes.SET_IMPERSONATING:
      return {
        ...state,
        isImpersonating: action.data.isImpersonating,
      };

    case UserTypes.SET_SHOW_TUTORIAL:
      return {
        ...state,
        showTutorial: action.data.showTutorial,
        tutorialStartStep: action.data.tutorialStartStep ?? 0,
      };

    case UserTypes.SET_ONBOARDING_SPOTLIGHT:
      return {
        ...state,
        onboardingSpotlight: action.data.onboardingSpotlight,
      };

    case UserTypes.LOGOUT_USER:
      return {
        ...state,
        ...userInitialState,
      };

    default:
      return state;
  }
};

export default userReducer;
